import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { CircleListResponse, RecitationLogResponse } from "@mubin/contracts";
import { Badge, Empty, Field, Loading, PageTitle, Tabs } from "../components/ui";
import { api, errorText } from "../lib/api";
import { useMe } from "../lib/session";

const iso = (d: Date) => d.toISOString().slice(0, 10);
const decisionLabel = { passed: "ناجح", repeat: "إعادة", partial: "جزئي" } as const;
const decisionTone = { passed: "success", repeat: "warning", partial: "info" } as const;
const kindLabel: Record<string, string> = {
  new: "حفظ جديد",
  near_review: "مراجعة قريبة",
  far_review: "مراجعة بعيدة",
  repeat: "إعادة",
};
const sessionLabel: Record<string, string> = {
  scheduled: "مجدولة",
  requested: "بانتظار المعلم",
  accepted: "قُبلت",
  connecting: "جارٍ الاتصال",
  active: "جارية",
  reconnecting: "إعادة اتصال",
  interrupted: "انقطعت",
  ended: "انتهت",
  cancelled: "أُلغيت",
  expired: "انتهت مهلتها",
  missed: "فاتت",
  failed: "فشلت",
};
const when = (s: string) =>
  new Date(s).toLocaleString("ar-SA-u-nu-latn", { dateStyle: "short", timeStyle: "short" });
const duration = (s: number | null) =>
  s === null ? "—" : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/** The recitation log (docs/09 §2): results (live and in person) and live sessions over a period. */
export function RecitationPage() {
  const { data: me } = useMe();
  const today = iso(new Date());
  const [from, setFrom] = useState(iso(new Date(Date.now() - 6 * 86_400_000)));
  const [to, setTo] = useState(today);
  const [circleId, setCircleId] = useState("");
  const [tab, setTab] = useState<"results" | "sessions">("results");
  const circles = useQuery({
    queryKey: ["circles", me?.activeRole],
    queryFn: () => api<CircleListResponse>("/v1/circles"),
  });
  const qs = new URLSearchParams({ from, to, ...(circleId ? { circleId } : {}) }).toString();
  const log = useQuery({
    queryKey: ["recitation-log", me?.activeRole, qs],
    queryFn: () => api<RecitationLogResponse>(`/v1/recitation-log?${qs}`),
    enabled: from <= to,
  });
  const d = log.data;

  return (
    <>
      <PageTitle title="التسميع" sub="نتائج التسميع المباشر والحضوري، وسجل الجلسات المباشرة، ضمن نطاقك." />
      <section className="card">
        <div className="form section">
          <div className="form-row">
            <Field id="rc-circle" label="الحلقة">
              <select id="rc-circle" value={circleId} onChange={(e) => setCircleId(e.target.value)}>
                <option value="">كل الحلقات</option>
                {circles.data?.circles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} · {c.branchName}
                  </option>
                ))}
              </select>
            </Field>
            <Field id="rc-from" label="من">
              <input
                id="rc-from"
                type="date"
                dir="ltr"
                value={from}
                max={to}
                onChange={(e) => setFrom(e.target.value)}
              />
            </Field>
            <Field id="rc-to" label="إلى">
              <input
                id="rc-to"
                type="date"
                dir="ltr"
                value={to}
                min={from}
                max={today}
                onChange={(e) => setTo(e.target.value)}
              />
            </Field>
          </div>
        </div>
      </section>

      <section className="card">
        <div className="toolbar">
          <Tabs
            label="العرض"
            value={tab}
            onChange={setTab}
            items={[
              { value: "results", label: `النتائج${d ? ` (${d.results.length})` : ""}` },
              { value: "sessions", label: `الجلسات المباشرة${d ? ` (${d.sessions.length})` : ""}` },
            ]}
          />
        </div>
        {d?.truncated && (
          <p className="muted small section">عُرضت أحدث 500 فقط؛ ضيّق الفترة أو اختر حلقة لعرض الباقي.</p>
        )}
        {log.isPending ? (
          <Loading />
        ) : log.isError ? (
          <Empty title="تعذر التحميل">{errorText(log.error)}</Empty>
        ) : tab === "results" ? (
          d!.results.length === 0 ? (
            <Empty title="لا نتائج في هذه الفترة" />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>الطالب</th>
                    <th>القرار</th>
                    <th>المقرر</th>
                    <th>الطريقة</th>
                    <th>المعلم</th>
                    <th>الحلقة</th>
                    <th>الوقت</th>
                  </tr>
                </thead>
                <tbody>
                  {d!.results.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <Link to="/users/$userId" params={{ userId: r.student.id }}>
                          {r.student.name}
                        </Link>
                      </td>
                      <td>
                        <Badge tone={decisionTone[r.decision]}>{decisionLabel[r.decision]}</Badge>
                        {r.revision > 1 && <span className="muted small"> (معدّلة)</span>}
                      </td>
                      <td className="small">
                        {[r.kind ? kindLabel[r.kind] : null, r.range].filter(Boolean).join(" · ") || "—"}
                      </td>
                      <td className="small">{r.live ? "مباشر" : "حضوري"}</td>
                      <td>{r.teacher.name}</td>
                      <td>{r.circle.name}</td>
                      <td className="num small">{when(r.recordedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : d!.sessions.length === 0 ? (
          <Empty title="لا جلسات مباشرة في هذه الفترة" />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>الطالب</th>
                  <th>الحالة</th>
                  <th>المعلم</th>
                  <th>الحلقة</th>
                  <th>المدة</th>
                  <th>الطلب</th>
                </tr>
              </thead>
              <tbody>
                {d!.sessions.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <Link to="/users/$userId" params={{ userId: s.student.id }}>
                        {s.student.name}
                      </Link>
                    </td>
                    <td>
                      <Badge
                        tone={
                          s.status === "ended"
                            ? "success"
                            : ["failed", "expired", "missed", "interrupted"].includes(s.status)
                              ? "warning"
                              : "neutral"
                        }
                      >
                        {sessionLabel[s.status] ?? s.status}
                      </Badge>
                    </td>
                    <td>{s.teacher?.name ?? <span className="muted">—</span>}</td>
                    <td>{s.circle.name}</td>
                    <td className="num small">{duration(s.durationSeconds)}</td>
                    <td className="num small">{s.requestedAt ? when(s.requestedAt) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
